import styled from '@emotion/styled';
import { ChevronRight, InfoOutlined } from '@mui/icons-material';
import {
  Alert,
  Box,
  Button,
  Card,
  IconButton,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Tooltip,
  TooltipProps,
  Typography,
  Zoom,
  tooltipClasses,
} from '@mui/material';
import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useExerciseStudents } from '../api';
import ksimConsole from '../assets/ksim-console.png';
import ksimUnhappy from '../assets/ksim-unhappy.png';
import Avatar from '../avatar/Avatar';
import TypeChip from '../chip/TypeChip';
import Fallback from '../fallback/Fallback';
import ManageStudentsDialog from '../manage-students-dialog/ManageStudentsDialog';
import { apiPaths, queryClient } from '../queryClient';
import { useSignalREffect } from '../signalr/SignalRContext';
import theme from '../theme';
import { ExerciseState, ParticipantNotification, SimulationEventMessage } from '../types';
import { useActiveCompany } from '../utils/useCompany';

export interface ExerciseStudentsProps {
  exerciseId: string;
  exerciseState: ExerciseState;
  simulatorId: string;
}

const LightTooltip = styled(({ className, ...props }: TooltipProps) => (
  <Tooltip {...props} classes={{ popper: className }} />
))(() => ({
  [`& .${tooltipClasses.tooltip}`]: {
    backgroundColor: 'white',
    color: 'rgba(0, 0, 0, 0.87)',
    fontSize: 11,
    border: '1px solid lightgray',
  },
}));

const ExerciseStudents = ({ exerciseId, exerciseState, simulatorId }: ExerciseStudentsProps) => {
  const navigate = useNavigate();
  const activeCompany = useActiveCompany();

  const [openStudentsDialog, setOpenStudentsDialog] = useState(false);
  const { students, isLoadingStudents, studentsError } = useExerciseStudents(exerciseId);

  const filteredStudents = students?.filter(user => user.isInvited) || [];

  const updateStudentsTable = (...args) => {
    const notification = args[0] as ParticipantNotification;
    const { userIds } = notification;
    if (userIds && userIds.some(id => students && students.some(user => user.id === id))) {
      queryClient.fetchQuery({
        queryKey: [
          {
            path: apiPaths.teach.users(activeCompany?.companyId, exerciseId),
          },
        ],
      });
    }
  };

  useSignalREffect(SimulationEventMessage.ParticipantSimulationStarted, updateStudentsTable, [students, activeCompany]);
  useSignalREffect(SimulationEventMessage.ParticipantSimulationCompleted, updateStudentsTable, [
    students,
    activeCompany,
  ]);

  return (
    <>
      <Stack alignItems="center" justifyContent="space-between" direction="row" gap={2} flexWrap="wrap" mb={4}>
        <Typography component="h2" variant="h5">
          Students
        </Typography>
        {exerciseState !== ExerciseState.Finished && (
          <Button variant="outlined" onClick={() => setOpenStudentsDialog(true)}>
            Add/remove students
          </Button>
        )}
      </Stack>
      {isLoadingStudents ? (
        <>Loading...</>
      ) : studentsError ? (
        <Fallback
          imgSrc={ksimUnhappy}
          message={
            <Alert severity="error">
              Something went wrong and the students and simulations couldn&lsquo;t be loaded. Please try again
            </Alert>
          }
        />
      ) : students?.length ? (
        <Card>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>Name</TableCell>
                <TableCell>Simulations</TableCell>
                <TableCell>Result</TableCell>
                <TableCell></TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {[
                ...students.filter(({ isInvited }) => isInvited),
                ...students.filter(({ isInvited }) => !isInvited),
              ].map(student => (
                <TableRow
                  key={student.id}
                  hover
                  onClick={() => navigate(`students/${student.id}`)}
                  sx={{
                    cursor: 'pointer',
                    textDecoration: 'none',
                    backgroundColor: student.isInvited
                      ? theme.palette.background.paper
                      : theme.palette.background.default,
                  }}
                >
                  <TableCell>
                    <Box
                      sx={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: 2,
                      }}
                    >
                      <Avatar src={student.avatar} />
                      {student.name}
                      {!student.isInvited && (
                        <LightTooltip
                          TransitionComponent={Zoom}
                          placement="right-end"
                          title="This user no longer has access to this exercise"
                        >
                          <IconButton>
                            <InfoOutlined fontSize="small" color="error" />
                          </IconButton>
                        </LightTooltip>
                      )}
                    </Box>
                  </TableCell>
                  <TableCell>{student.numberOfAttempts}</TableCell>
                  <TableCell>
                    {student.numberOfAttempts > 0 && (
                      <TypeChip label={student.result} type={student.result} sx={{ textTransform: 'none' }} />
                    )}
                  </TableCell>
                  <TableCell align="right">
                    <ChevronRight sx={theme => ({ color: theme.palette.text.secondary })} />
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </Card>
      ) : (
        <Fallback imgSrc={ksimConsole} message="No students have been added yet" />
      )}

      <ManageStudentsDialog
        exerciseId={exerciseId!}
        onConfirm={() => setOpenStudentsDialog(false)}
        open={openStudentsDialog}
        onClose={() => setOpenStudentsDialog(false)}
        simulatorId={simulatorId}
        studentIds={filteredStudents.map(student => student.id)}
      />
    </>
  );
};

export default ExerciseStudents;
